import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Brain, AlertTriangle, TrendingUp, Lightbulb, Target,
  RefreshCw, Zap, Activity, ChevronRight, Clock, Shield,
  CheckCircle2, XCircle, AlertCircle, BarChart3, FileText,
  Cpu, Sparkles, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

// Confidence level config
const confidenceConfig = {
  very_high: { color: 'bg-green-500', badge: 'bg-green-100 text-green-800', label: 'Very High' },
  high: { color: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800', label: 'High' },
  medium: { color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800', label: 'Medium' },
  low: { color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', label: 'Low' },
};

// Severity config
const severityConfig = {
  critical: { color: 'bg-red-500', badge: 'bg-red-100 text-red-800' },
  high: { color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800' },
  moderate: { color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
  low: { color: 'bg-green-500', badge: 'bg-green-100 text-green-800' },
};

export default function AIRiskPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Predictions
  const [predictions, setPredictions] = useState([]);
  
  // Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Analysis history
  const [history, setHistory] = useState([]);

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch dashboard
  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-risk/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch predictions
  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-risk/predictions`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setPredictions(data.predictions || []);
      }
    } catch (err) {
      console.error('Error fetching predictions:', err);
    }
  };

  // Fetch history
  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-risk/history`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  // Run analysis
  const runAnalysis = async (analysisType) => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-risk/analyze/${analysisType}`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        setAnalysisResult(data);
        toast.success(`${analysisType.replace('-', ' ')} analysis completed`);
        await fetchHistory();
      } else {
        toast.error('Analysis failed');
      }
    } catch (err) {
      toast.error('Error running analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  // Generate predictions
  const generatePredictions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-risk/predictions/generate`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchPredictions();
      } else {
        toast.error('Failed to generate predictions');
      }
    } catch (err) {
      toast.error('Error generating predictions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all data
  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchPredictions(),
      fetchHistory()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [token]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Confidence badge
  const ConfidenceBadge = ({ level }) => {
    const config = confidenceConfig[level] || confidenceConfig.medium;
    return <Badge className={config.badge}>{config.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="ai-risk-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            {t('ai.riskPrediction')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('ai.hybridSystem')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchAll}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button 
            onClick={generatePredictions}
            disabled={loading}
            data-testid="generate-predictions-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {t('ai.generatePredictions')}
          </Button>
        </div>
      </div>

      {/* LLM Status */}
      {dashboard && (
        <Card className={dashboard.llm_status === 'available' ? 'border-green-500' : 'border-orange-500'}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cpu className={`h-6 w-6 ${dashboard.llm_status === 'available' ? 'text-green-500' : 'text-orange-500'}`} />
                <div>
                  <p className="font-medium">{t('ai.engineStatus')}</p>
                  <p className="text-sm text-gray-500">
                    Model: {dashboard.model_version} | OpenAI GPT-5.2
                  </p>
                </div>
              </div>
              <Badge className={dashboard.llm_status === 'available' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                {dashboard.llm_status === 'available' ? t('ai.online') : t('ai.offline')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-predictions">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('ai.totalPredictions')}</p>
                  <p className="text-2xl font-bold">{dashboard.total_predictions}</p>
                </div>
                <Target className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-high-confidence">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('ai.highConfidence')}</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard.high_confidence_predictions}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-analyses">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('ai.totalAnalyses')}</p>
                  <p className="text-2xl font-bold">{dashboard.total_analyses}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-capabilities">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('ai.capabilities')}</p>
                  <p className="text-2xl font-bold">{dashboard.capabilities?.length || 0}</p>
                </div>
                <Zap className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <Activity className="h-4 w-4 mr-2" />
            {t('common.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="analyze" data-testid="tab-analyze">
            <Brain className="h-4 w-4 mr-2" />
            {t('ai.analyzeRisk')}
          </TabsTrigger>
          <TabsTrigger value="predictions" data-testid="tab-predictions">
            <Target className="h-4 w-4 mr-2" />
            {t('ai.predictions')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            {t('common.history')}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Capabilities */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  {t('ai.aiCapabilities')}
                </CardTitle>
                <CardDescription>{t('ai.whatCanAnalyze')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(dashboard?.capabilities || []).map((cap, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{cap.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Analyses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  {t('ai.recentAnalyses')}
                </CardTitle>
                <CardDescription>{t('ai.latestRuns')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {(dashboard?.recent_analyses || []).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">{t('ai.noAnalysesYet')}</p>
                  ) : (
                    <div className="space-y-3">
                      {(dashboard?.recent_analyses || []).map((analysis, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge variant="outline" className="mb-2">
                                {analysis.request_type}
                              </Badge>
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {analysis.summary}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(analysis.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Predictions by Type */}
          {dashboard && Object.keys(dashboard.predictions_by_type || {}).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Predictions by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(dashboard.predictions_by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <Target className="h-5 w-5 text-purple-500" />
                      <span className="font-medium capitalize">{type}:</span>
                      <span className="text-gray-600 dark:text-gray-400">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Analyze Tab */}
        <TabsContent value="analyze" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Trend Analysis */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => runAnalysis('trend')}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Trend Analysis</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Identify patterns and trends in safety data over time
                    </p>
                    <Button className="mt-4" disabled={analyzing} data-testid="run-trend-analysis">
                      {analyzing ? 'Analyzing...' : 'Run Analysis'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Root Cause Analysis */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => runAnalysis('root-cause')}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Root Cause Analysis</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Identify underlying causes of safety issues
                    </p>
                    <Button className="mt-4" disabled={analyzing} data-testid="run-rca">
                      {analyzing ? 'Analyzing...' : 'Run Analysis'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => runAnalysis('recommendations')}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Lightbulb className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Get Recommendations</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      AI-powered safety improvement recommendations
                    </p>
                    <Button className="mt-4" disabled={analyzing} data-testid="get-recommendations">
                      {analyzing ? 'Analyzing...' : 'Get Recommendations'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Prediction */}
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={generatePredictions}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Target className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Generate Risk Predictions</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Predict potential future risks based on historical data
                    </p>
                    <Button className="mt-4" disabled={loading} data-testid="generate-risk-predictions">
                      {loading ? 'Generating...' : 'Generate Predictions'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analysis Result */}
          {analysisResult && (
            <Card className="border-purple-500">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  Analysis Result
                </CardTitle>
                <CardDescription>
                  Type: {analysisResult.request_type} | Confidence: {(analysisResult.confidence_score * 100).toFixed(0)}%
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary */}
                <div>
                  <h4 className="font-semibold mb-2">Summary</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                    {analysisResult.summary}
                  </p>
                </div>

                {/* Recommendations */}
                {analysisResult.recommendations?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Recommendations</h4>
                    <div className="space-y-2">
                      {analysisResult.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <ChevronRight className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Insights */}
                {analysisResult.insights?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Insights</h4>
                    <div className="space-y-2">
                      {analysisResult.insights.map((insight, idx) => (
                        <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <Badge variant="outline" className="mb-2">{insight.source}</Badge>
                          {insight.patterns && (
                            <div className="space-y-1">
                              {insight.patterns.map((pattern, pidx) => (
                                <div key={pidx} className="text-sm">
                                  <span className="font-medium">{pattern.type}:</span> {pattern.finding}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing info */}
                <div className="text-xs text-gray-400 pt-2 border-t">
                  Processed in {analysisResult.processing_time_ms}ms | Tokens: {analysisResult.tokens_used}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Risk Predictions</h2>
            <Button onClick={generatePredictions} disabled={loading}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate New
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {predictions.map(prediction => (
              <Card key={prediction.id} data-testid={`prediction-${prediction.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        prediction.severity_if_occurs === 'critical' ? 'bg-red-100' :
                        prediction.severity_if_occurs === 'high' ? 'bg-orange-100' :
                        'bg-yellow-100'
                      }`}>
                        <AlertTriangle className={`h-5 w-5 ${
                          prediction.severity_if_occurs === 'critical' ? 'text-red-600' :
                          prediction.severity_if_occurs === 'high' ? 'text-orange-600' :
                          'text-yellow-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{prediction.title}</h3>
                        <Badge variant="outline" className="mt-1">{prediction.prediction_type}</Badge>
                      </div>
                    </div>
                    <ConfidenceBadge level={prediction.confidence} />
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {prediction.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-500">Probability</p>
                      <div className="flex items-center gap-2">
                        <Progress value={prediction.probability} className="h-2 flex-1" />
                        <span className="font-medium">{prediction.probability}%</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500">Timeframe</p>
                      <p className="font-medium">{prediction.timeframe}</p>
                    </div>
                  </div>

                  {prediction.recommended_actions?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Recommended Actions</p>
                      <div className="space-y-1">
                        {prediction.recommended_actions.slice(0, 3).map((action, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-400 mt-4 pt-2 border-t">
                    Generated: {formatDate(prediction.created_at)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {predictions.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No predictions generated yet.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Generate Predictions" to analyze safety data.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <h2 className="text-lg font-semibold">Analysis History</h2>

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-left py-3 px-4">Summary</th>
                      <th className="text-left py-3 px-4">Confidence</th>
                      <th className="text-left py-3 px-4">Processing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(item => (
                      <tr key={item.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="py-3 px-4">{formatDate(item.created_at)}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{item.request_type}</Badge>
                        </td>
                        <td className="py-3 px-4 max-w-xs truncate">{item.summary}</td>
                        <td className="py-3 px-4">
                          <Progress value={item.confidence_score * 100} className="h-2 w-20" />
                        </td>
                        <td className="py-3 px-4 text-gray-500">{item.processing_time_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {history.length === 0 && (
                <div className="py-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No analysis history yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
